import { api } from '../../lib/api-client';

/** Every stream an AutoCount sales line can count in (spec §3). */
export const STREAMS = ['BIKES', 'PARTS', 'LABOUR', 'FEES', 'COLLECTED', 'FINANCE', 'UNCLASSIFIED'] as const;
export type Stream = (typeof STREAMS)[number];

/**
 * The streams that add up to revenue, in the order they stack. COLLECTED is
 * money held for somebody else and FINANCE is a deduction: neither is revenue.
 */
export const REVENUE_STREAMS: readonly Stream[] = ['BIKES', 'PARTS', 'LABOUR', 'FEES', 'UNCLASSIFIED'];

export interface StreamFigure {
  revenue: string;
  cost: string;
  costedRevenue: string;
}

export interface SalesMonth {
  month: number;
  byStream: Record<Stream, StreamFigure>;
  lastYearRevenue: string;
}

export interface SalesSummary {
  dataAsOf: string | null;
  year: number;
  months: SalesMonth[];
  totals: {
    revenue: string;
    lastYearRevenue: string;
    grossProfit: string;
    /** Share of revenue that carries a cost, 0..1 to 4 dp. */
    costCoverage: string;
    comparedThroughMonth: number;
  };
}

export interface SalesGroup {
  itemGroup: string | null;
  stream: Stream;
  /** Management chose this group's stream; false is the default rule. */
  mapped: boolean;
  revenue: string;
  lines: number;
}

export interface SalesLine {
  docType: string;
  docNo: string;
  docDate: string;
  debtorName: string | null;
  itemCode: string | null;
  description: string | null;
  itemGroup: string | null;
  stream: Stream;
  qty: string;
  revenue: string;
  cost: string | null;
}

export interface SalesLinesPage {
  items: SalesLine[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface StreamMapping {
  itemGroup: string;
  stream: Stream;
  isDefault: boolean;
}

/** What a drill-down asks for. An empty itemGroup asks for lines with no group. */
export interface SalesLinesFilter {
  year: number;
  month?: number;
  stream?: Stream;
  itemGroup?: string;
  itemCode?: string;
}

function linesQuery(filter: SalesLinesFilter, extra: Record<string, string>): string {
  const params = new URLSearchParams({ year: String(filter.year) });
  if (filter.month !== undefined) params.set('month', String(filter.month));
  if (filter.stream !== undefined) params.set('stream', filter.stream);
  if (filter.itemGroup !== undefined) params.set('itemGroup', filter.itemGroup);
  if (filter.itemCode !== undefined) params.set('itemCode', filter.itemCode);
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  return params.toString();
}

export const fetchSalesSummary = (year: number): Promise<SalesSummary> =>
  api<SalesSummary>(`/analytics/sales/summary?year=${String(year)}`);

export const fetchSalesByGroup = (year: number): Promise<SalesGroup[]> =>
  api<SalesGroup[]>(`/analytics/sales/by-group?year=${String(year)}`);

export const fetchSalesLines = (filter: SalesLinesFilter, cursor?: string): Promise<SalesLinesPage> =>
  api<SalesLinesPage>(`/analytics/sales/lines?${linesQuery(filter, cursor === undefined ? {} : { cursor })}`);

export const exportSalesLinesCsv = (filter: SalesLinesFilter): Promise<string> =>
  api<string>(`/analytics/sales/lines?${linesQuery(filter, { format: 'csv' })}`);

/** Groups contain '/', as in R/TAX, so the group is encoded into the path. */
export const setStream = (itemGroup: string, stream: Stream): Promise<StreamMapping> =>
  api<StreamMapping>(`/analytics/streams/${encodeURIComponent(itemGroup)}`, {
    method: 'PUT',
    body: { stream },
  });

export const RECEIVABLE_BUCKETS = ['CURRENT', 'D1_30', 'D31_60', 'D61_90', 'D90_PLUS'] as const;
export type ReceivableBucket = (typeof RECEIVABLE_BUCKETS)[number];

export const BIKE_AGE_BUCKETS = ['D0_90', 'D91_180', 'D181_365', 'D365_PLUS', 'UNKNOWN'] as const;
export type BikeAgeBucket = (typeof BIKE_AGE_BUCKETS)[number];

export interface ReceivableBucketFigure {
  bucket: ReceivableBucket;
  amount: string;
  items: number;
}

/** One side of the books, receivables or payables, in the shape both tabs draw. */
export type Ledger = 'AR' | 'AP';

export interface LedgerParty {
  code: string;
  name: string | null;
  owed: string;
  over90: string;
  oldestDaysPastDue: number;
}

export interface LedgerSummary {
  dataAsOf: string | null;
  owed: string;
  openItems: number;
  parties: number;
  buckets: ReceivableBucketFigure[];
  /** Receipts or supplier payments not yet applied: shown beside what is owed, never netted. */
  unapplied: { amount: string; count: number };
  /** Credit notes not yet matched to an invoice: shown beside what is owed, never netted. */
  creditNotes: { amount: string; count: number };
  topParties: LedgerParty[];
}

export interface LedgerItem {
  uid: string;
  kind: string;
  docNo: string;
  supplierInvoiceNo: string | null;
  docDate: string;
  dueDate: string | null;
  partyCode: string;
  partyName: string | null;
  amount: string;
  daysPastDue: number | null;
  bucket: string | null;
}

export interface LedgerItemsPage {
  items: LedgerItem[];
  nextCursor: string | null;
}

export interface LedgerItemsFilter {
  kind: 'OPEN' | 'UNAPPLIED' | 'CREDIT_NOTES';
  partyCode?: string;
  bucket?: ReceivableBucket;
}

interface ReceivablesSummaryWire {
  dataAsOf: string | null;
  owed: string;
  openItems: number;
  debtors: number;
  buckets: ReceivableBucketFigure[];
  unapplied: { amount: string; receipts: number };
  unmatchedCreditNotes: { amount: string; creditNotes: number };
  topDebtors: { debtorCode: string; debtorName: string | null; owed: string; over90: string; oldestDaysPastDue: number }[];
}

interface PayablesSummaryWire {
  dataAsOf: string | null;
  owed: string;
  openItems: number;
  suppliers: number;
  buckets: ReceivableBucketFigure[];
  unapplied: { amount: string; payments: number };
  unmatchedCreditNotes: { amount: string; creditNotes: number };
  topSuppliers: {
    creditorCode: string;
    creditorName: string | null;
    owed: string;
    over90: string;
    oldestDaysPastDue: number;
  }[];
}

interface ItemWire {
  uid: string;
  kind: string;
  docNo: string;
  supplierInvoiceNo?: string | null;
  docDate: string;
  dueDate: string | null;
  debtorCode?: string;
  debtorName?: string | null;
  creditorCode?: string;
  creditorName?: string | null;
  amount: string;
  daysPastDue: number | null;
  bucket: string | null;
}

export interface BikesSold {
  month: number;
  brand: string | null;
  units: string;
}

export interface BikeStockBucket {
  bucket: BikeAgeBucket;
  bikes: number;
  cost: string;
}

export interface BikesSummary {
  salesDataAsOf: string | null;
  stockDataAsOf: string | null;
  sold: BikesSold[];
  unitsThisYear: string;
  unitsLastYearSamePeriod: string;
  comparedThroughMonth: number;
  stock: { bikes: number; cost: string; buckets: BikeStockBucket[] };
}

export interface BikeStockItem {
  uid: string;
  itemCode: string;
  description: string | null;
  brand: string | null;
  chassisNo: string;
  engineNo: string | null;
  purchaseDocNo: string | null;
  purchasedOn: string | null;
  daysInStock: number | null;
  bucket: BikeAgeBucket;
  unitCost: string | null;
}

export interface BikeStockPage {
  items: BikeStockItem[];
  nextCursor: string | null;
}

export interface BikeStockFilter {
  bucket?: BikeAgeBucket;
  brand?: string;
}

function ledgerItemsQuery(
  filter: LedgerItemsFilter,
  partyParam: 'debtorCode' | 'creditorCode',
  extra: Record<string, string>,
): string {
  const params = new URLSearchParams({ kind: filter.kind });
  if (filter.partyCode !== undefined) params.set(partyParam, filter.partyCode);
  if (filter.bucket !== undefined) params.set('bucket', filter.bucket);
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  return params.toString();
}

function bikeStockQuery(filter: BikeStockFilter, extra: Record<string, string>): string {
  const params = new URLSearchParams();
  if (filter.bucket !== undefined) params.set('bucket', filter.bucket);
  if (filter.brand !== undefined) params.set('brand', filter.brand);
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  return params.toString();
}

export const fetchReceivablesSummary = async (): Promise<LedgerSummary> => {
  const w = await api<ReceivablesSummaryWire>('/analytics/receivables/summary');
  return {
    dataAsOf: w.dataAsOf,
    owed: w.owed,
    openItems: w.openItems,
    parties: w.debtors,
    buckets: w.buckets,
    unapplied: { amount: w.unapplied.amount, count: w.unapplied.receipts },
    creditNotes: { amount: w.unmatchedCreditNotes.amount, count: w.unmatchedCreditNotes.creditNotes },
    topParties: w.topDebtors.map((d) => ({
      code: d.debtorCode,
      name: d.debtorName,
      owed: d.owed,
      over90: d.over90,
      oldestDaysPastDue: d.oldestDaysPastDue,
    })),
  };
};

export const fetchPayablesSummary = async (): Promise<LedgerSummary> => {
  const w = await api<PayablesSummaryWire>('/analytics/payables/summary');
  return {
    dataAsOf: w.dataAsOf,
    owed: w.owed,
    openItems: w.openItems,
    parties: w.suppliers,
    buckets: w.buckets,
    unapplied: { amount: w.unapplied.amount, count: w.unapplied.payments },
    creditNotes: { amount: w.unmatchedCreditNotes.amount, count: w.unmatchedCreditNotes.creditNotes },
    topParties: w.topSuppliers.map((d) => ({
      code: d.creditorCode,
      name: d.creditorName,
      owed: d.owed,
      over90: d.over90,
      oldestDaysPastDue: d.oldestDaysPastDue,
    })),
  };
};

const toLedgerItem = (w: ItemWire): LedgerItem => ({
  uid: w.uid,
  kind: w.kind,
  docNo: w.docNo,
  supplierInvoiceNo: w.supplierInvoiceNo ?? null,
  docDate: w.docDate,
  dueDate: w.dueDate,
  partyCode: w.debtorCode ?? w.creditorCode ?? '',
  partyName: w.debtorName ?? w.creditorName ?? null,
  amount: w.amount,
  daysPastDue: w.daysPastDue,
  bucket: w.bucket,
});

async function fetchLedgerItems(
  path: string,
  partyParam: 'debtorCode' | 'creditorCode',
  filter: LedgerItemsFilter,
  cursor: string | undefined,
): Promise<LedgerItemsPage> {
  const page = await api<{ items: ItemWire[]; nextCursor: string | null }>(
    `${path}?${ledgerItemsQuery(filter, partyParam, cursor === undefined ? {} : { cursor })}`,
  );
  return { items: page.items.map(toLedgerItem), nextCursor: page.nextCursor };
}

export const fetchReceivableItems = (filter: LedgerItemsFilter, cursor?: string): Promise<LedgerItemsPage> =>
  fetchLedgerItems('/analytics/receivables/items', 'debtorCode', filter, cursor);

export const fetchPayableItems = (filter: LedgerItemsFilter, cursor?: string): Promise<LedgerItemsPage> =>
  fetchLedgerItems('/analytics/payables/items', 'creditorCode', filter, cursor);

export const exportReceivableItemsCsv = (filter: LedgerItemsFilter): Promise<string> =>
  api<string>(`/analytics/receivables/items?${ledgerItemsQuery(filter, 'debtorCode', { format: 'csv' })}`);

export const exportPayableItemsCsv = (filter: LedgerItemsFilter): Promise<string> =>
  api<string>(`/analytics/payables/items?${ledgerItemsQuery(filter, 'creditorCode', { format: 'csv' })}`);

export const fetchBikesSummary = (year: number): Promise<BikesSummary> =>
  api<BikesSummary>(`/analytics/bikes/summary?year=${String(year)}`);

export const fetchBikeStock = (filter: BikeStockFilter, cursor?: string): Promise<BikeStockPage> =>
  api<BikeStockPage>(
    `/analytics/bikes/stock?${bikeStockQuery(filter, cursor === undefined ? {} : { cursor })}`,
  );

export const exportBikeStockCsv = (filter: BikeStockFilter): Promise<string> =>
  api<string>(`/analytics/bikes/stock?${bikeStockQuery(filter, { format: 'csv' })}`);

export interface TopSeller {
  itemCode: string;
  description: string | null;
  qty: string;
  revenue: string;
}

export interface PartsSummary {
  stockDataAsOf: string | null;
  salesDataAsOf: string | null;
  stockValue: string;
  partsInStock: number;
  negativeParts: number;
  deadStock: { parts: number; value: string };
  topSellers: TopSeller[];
}

export interface DeadStockItem {
  skuCode: string;
  name: string;
  qty: string;
  value: string;
  lastSoldOn: string | null;
}

export interface DeadStockPage {
  items: DeadStockItem[];
  nextCursor: string | null;
}

export const fetchPartsSummary = (year: number): Promise<PartsSummary> =>
  api<PartsSummary>(`/analytics/parts/summary?year=${String(year)}`);

export const fetchDeadStock = (cursor?: string): Promise<DeadStockPage> =>
  api<DeadStockPage>(
    `/analytics/parts/dead-stock${cursor === undefined ? '' : `?${new URLSearchParams({ cursor }).toString()}`}`,
  );

export const exportDeadStockCsv = (): Promise<string> => api<string>('/analytics/parts/dead-stock?format=csv');

export interface PurchaseMonth {
  year: number;
  month: number;
  byStream: Record<Stream, string>;
}

export interface PurchaseSupplier {
  creditorCode: string | null;
  creditorName: string | null;
  amount: string;
}

export interface PurchasesSummary {
  dataAsOf: string | null;
  /** Last year then this year, twelve months each. */
  months: PurchaseMonth[];
  thisYear: string;
  sameMonthsLastYear: string;
  comparedThroughMonth: number;
  bikesBought: { month: number; units: string }[];
  bikesSold: { month: number; units: string }[];
  partsBought: { month: number; amount: string }[];
  partsCostSold: { month: number; cost: string }[];
  topSuppliers: PurchaseSupplier[];
}

export interface PurchaseLine {
  uid: string;
  docNo: string;
  supplierInvoiceNo: string | null;
  docDate: string;
  creditorCode: string | null;
  creditorName: string | null;
  itemCode: string | null;
  description: string | null;
  itemGroup: string | null;
  stream: Stream;
  qty: string;
  amount: string;
}

export interface PurchaseLinesPage {
  items: PurchaseLine[];
  nextCursor: string | null;
}

export interface PurchaseLinesFilter {
  year: number;
  month?: number;
  stream?: Stream;
  creditorCode?: string;
}

function purchaseLinesQuery(filter: PurchaseLinesFilter, extra: Record<string, string>): string {
  const params = new URLSearchParams({ year: String(filter.year) });
  if (filter.month !== undefined) params.set('month', String(filter.month));
  if (filter.stream !== undefined) params.set('stream', filter.stream);
  if (filter.creditorCode !== undefined) params.set('creditorCode', filter.creditorCode);
  for (const [key, value] of Object.entries(extra)) params.set(key, value);
  return params.toString();
}

export const fetchPurchasesSummary = (year: number): Promise<PurchasesSummary> =>
  api<PurchasesSummary>(`/analytics/purchases/summary?year=${String(year)}`);

export const fetchPurchaseLines = (filter: PurchaseLinesFilter, cursor?: string): Promise<PurchaseLinesPage> =>
  api<PurchaseLinesPage>(
    `/analytics/purchases/lines?${purchaseLinesQuery(filter, cursor === undefined ? {} : { cursor })}`,
  );

export const exportPurchaseLinesCsv = (filter: PurchaseLinesFilter): Promise<string> =>
  api<string>(`/analytics/purchases/lines?${purchaseLinesQuery(filter, { format: 'csv' })}`);

// ── Operations (spec 2026-10-27): the DMS's own work ─────────────────────

export type OperationsMetric =
  | 'LEADS_CREATED'
  | 'LEADS_PAST_FIRST_CONTACT'
  | 'LEADS_PAST_NEXT_ACTION'
  | 'QUOTATIONS_SENT'
  | 'BOOKINGS_CONFIRMED'
  | 'BOOKINGS_OPEN'
  | 'BIKES_DELIVERED'
  | 'REGISTRATION_AP'
  | 'REGISTRATION_PUSPAKOM'
  | 'REGISTRATION_JPJ'
  | 'JOBS_OPEN'
  | 'JOBS_OPEN_OVER_3_DAYS'
  | 'JOBS_CLOSED'
  | 'JOBS_COMEBACK'
  | 'PARTS_REQUESTS_WAITING'
  | 'INVOICES_ISSUED'
  | 'INVOICES_UNPAID'
  | 'DOCUMENTS_NOT_HANDED_OVER'
  | 'CASES_OPEN'
  | 'CASES_LATE'
  | 'REMINDERS_DUE';

export interface MoneyFigure {
  count: number;
  total: string;
}

export interface OperationsSummary {
  asOf: string;
  from: string;
  to: string;
  /** The branch actually counted; null for all. */
  branchUid: string | null;
  /** Every active branch, for someone who can view all; else empty. */
  branches: { uid: string; name: string }[];
  sales: {
    leadsCreated: number;
    leadsBySource: { key: string; count: number }[];
    leadsPastFirstContact: number;
    leadsPastNextAction: number;
    /** Late on either count, each lead once. */
    leadsLate: number;
    quotationsSent: number;
    bookingsConfirmed: number;
    bookingsOpen: number;
    bikesDelivered: number;
    /** AutoCount, every branch: AutoCount carries none. Shown beside deliveries, never added. */
    autocountBikesSold: number;
    autocountAsOf: string | null;
  };
  registration: { stage: 'AP' | 'PUSPAKOM' | 'JPJ'; waiting: number; overdue: number }[];
  workshop: {
    waiting: number;
    inWork: number;
    waitingPartsOrApproval: number;
    /** Ready to bill, to collect, or completed but not yet closed. */
    billToClose: number;
    open: number;
    openOver3Days: number;
    closed: number;
    comebacks: number;
    technicians: { name: string; hours: number }[];
  };
  till: { partsRequestsWaiting: number; invoicesIssued: MoneyFigure; unpaid: MoneyFigure; notHandedOver: number };
  service: { casesOpen: number; casesLate: number; remindersDue: number };
}

export type OperationsRowKind =
  | 'lead'
  | 'quotation'
  | 'booking'
  | 'delivery'
  | 'registration'
  | 'job'
  | 'parts-request'
  | 'invoice'
  | 'handover'
  | 'case'
  | 'reminder';

export interface OperationsRow {
  uid: string;
  ref: string;
  title: string | null;
  subtitle: string | null;
  at: string | null;
  status: string | null;
  amount: string | null;
}

export interface OperationsRows {
  metric: OperationsMetric;
  kind: OperationsRowKind;
  rows: OperationsRow[];
  truncated: boolean;
  asOf: string;
}

const branchParam = (branch: string | null): string => (branch === null ? '' : `branch=${branch}`);

export const fetchOperations = (branch: string | null): Promise<OperationsSummary> =>
  api<OperationsSummary>(`/analytics/operations?${branchParam(branch)}`);

export const fetchOperationsRows = (metric: OperationsMetric, branch: string | null): Promise<OperationsRows> =>
  api<OperationsRows>(`/analytics/operations/rows?metric=${metric}&${branchParam(branch)}`);

export const exportOperationsRowsCsv = (metric: OperationsMetric, branch: string | null): Promise<string> =>
  api<string>(`/analytics/operations/rows?metric=${metric}&format=csv&${branchParam(branch)}`);

// ─── exceptions (spec 2026-10-29) ──────────────────────────────────────

export type ExceptionKey =
  | 'LEADS_PAST_FIRST_CONTACT'
  | 'LEADS_PAST_NEXT_ACTION'
  | 'REGISTRATION_OVERDUE'
  | 'JOBS_OPEN_OVER_3_DAYS'
  | 'DOCUMENTS_NOT_HANDED_OVER'
  | 'CASES_LATE'
  | 'BRIDGE_SILENT'
  | 'AMOUNT_DIFFERENCES'
  | 'NOT_POSTED'
  | 'STOCK_DIFFERENCES'
  | 'BIKE_SALES_TO_CHECK'
  | 'ITEM_CODES_TO_MATCH';

export interface ExceptionLine {
  key: ExceptionKey;
  count: number;
  area: 'DMS' | 'AUTOCOUNT';
  metric: OperationsMetric | null;
  link: string | null;
}

export interface Exceptions {
  asOf: string;
  branchUid: string | null;
  branches: { uid: string; name: string }[];
  lines: ExceptionLine[];
  dmsTotal: number;
  autocountTotal: number;
}

export const fetchExceptions = (branch: string | null): Promise<Exceptions> =>
  api<Exceptions>(`/analytics/exceptions?${branchParam(branch)}`);
